import { Component, Inject, Input, OnInit, Output, EventEmitter, OnChanges, SimpleChanges, HostListener, ViewChild, ElementRef, OnDestroy } from '@angular/core';
import { I18NEXT_SERVICE, ITranslationService } from 'angular-i18next';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { AuthService, OAuthStorage } from 'src/app/auth';
import { HomeSearchComponent } from 'src/app/shared/home-search/home-search.component';
import { Router } from '@angular/router';
import { HeaderService } from './header.service';
import { interval, Subject, Subscription, timer } from 'rxjs';
import { UtilService } from 'src/app/services/util.service';
import { CommonWindow, ContractsNotifications } from '@interfaces';
import { MatIconRegistry } from '@angular/material/icon';
import { DomSanitizer } from '@angular/platform-browser';
import { takeUntil } from 'rxjs/operators';
import { ImpersonalizeUserComponent } from 'src/app/modules/impersonalize-user/impersonalize-user.component';
import { LanguagePickerComponent } from 'src/app/modules/language-picker/language-picker.component';
import { ModalChooseCountryComponent } from '@shared/modal-choose-country/modal-choose-country.component';
import { ToastrService } from '../toastr/toastr.service';
import { ChangePasswordComponent } from 'src/app/modules/change-password/change-password.component';
import { ModalDeleteFactorsComponent } from '@shared/modal-delete-factors/modal-delete-factors.component';

@Component({
  selector: 'app-header',
  templateUrl: './header.component.html'
})
export class HeaderComponent implements OnInit, OnChanges, OnDestroy {
  public isMenuOpen = false;
  public showSearch = false;
  public routeLabel: string = null;
  public route: any;
  public isExternal: boolean;
  public name: string;
  public role: string;
  public contractsNotifications: ContractsNotifications[] = [];
  public openedNotifications = false;
  public onlyCanRead: boolean;
  public isUserSpanish = true;
  public isLoading = false;
  public isAllowImpersonalization = false;
  public isResponsable = false;
  public notComercial: boolean;
  public multipleCountries = false;
  public isOneLoginUser = true;

  private excelId: number;
  private nofificationsSubscription: Subscription;
  private destroyed$: Subject<boolean> = new Subject<boolean>();

  @Input() currentRoute = null;
  @Input() avatar: string;
  @Output() toggleMenu = new EventEmitter<boolean>();
  @ViewChild('notificationsPanel') notificationsPanel: ElementRef;
  @ViewChild('notificationsButton') notificationsButton: ElementRef;

  constructor(
    private auth: AuthService,
    private dialog: MatDialog,
    public router: Router,
    private headerService: HeaderService,
    private utilService: UtilService,
    private iconRegistry: MatIconRegistry,
    private sanitizer: DomSanitizer,
    private authStorage: OAuthStorage,
    private toastrService: ToastrService,
    @Inject(I18NEXT_SERVICE) private i18NextService: ITranslationService,
    @Inject('Window') protected window: CommonWindow,
  ) { }

  ngOnInit(): void {
    this.isUserSpanish = this.auth.getUserData().country === 'ES';
    this.isOneLoginUser = this.auth.getUserData().userOnelogin;
    this.notComercial = this.auth.getRole().code !== '300000004';
    this.isResponsable = this.auth.getUserData().isResponsable;
    this.multipleCountries = +this.authStorage.getItem('num_countries') > 1;
    this.registerIcons();
    this.isAllowImpersonalization = !!this.authStorage.getItem('authorization_imp_token')
      || this.auth.getUserData().permitirImpersonalizacion;
    this.name = this.auth.getUserData().fullName;
    this.role = this.auth.getRole().value;
    this.onlyCanRead = this.auth.getOnlyCanRead();
    this.isExternal = this.auth.getRole().suffix === '-pymes';
    if (this.isExternal) {
      this.getNotifications();
    }
    this.subscribeDownloadContractsExcel();
  }

  ngOnChanges(changes: SimpleChanges): void {
    this.setRouteLabel();
    this.showSearch = ['/home-pymes', '/home-large-accounts'].includes(this.currentRoute);
  }

  ngOnDestroy() {
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  @HostListener('document:click', ['$event'])
  clickout(event) {
    if (this.openedNotifications && event.target !== this.notificationsPanel.nativeElement &&
      event.target !== this.notificationsButton.nativeElement
    ) {
      this.handleNotifications();
    }
  }

  private getNotifications(): void {
    this.getContractsPending(false);
    this.headerService.getNotification().subscribe(
      res => {
        if (res) {
          this.getContractsPending(false);
          this.headerService.setNotification(false);
        }
      }
    );
    this.nofificationsSubscription = interval(100000).subscribe(() => {
      this.getContractsPending(false);
    });
  }

  private getContractsPending(checkDownloadContractsExcel: boolean): void {
    this.headerService.getContractsPending(this.isUserSpanish ? 'contrato.getContractsPending' : 'contrato.getContractsPendingPT').subscribe(
      response => {
        if (response.success) {
          this.contractsNotifications = response.data?.map(value => ({
            socialReason: value.nombreRS,
            campaign: value.nombreCampaña,
            level: value.nombreNivel,
            codeCRM: value.codigoCRMContrato,
            registerCRM: value.registroEnCRM,
            resultOk: value.resultOk,
            descriptionError: value.descripcionErrorRegistroEnCRM,
            descargadoSEPA: value.descargadoSEPA,
            cuentasCorrientesSEPA: value.cuentasCorrientesSEPA,
            hasChangedDni: value.hasChangedDni,
            cambioProducto: value.cambioProducto,
            renovacion: value.renovacion,
            idFichero: value.idFichero,
            nombreFichero: value.nombreFichero,
            formPago: value.formPago
          }));
          if (checkDownloadContractsExcel) {
            this.finishcheckDownloadContractsExcel();
          }
        } else {
          this.utilService.toastErrors(response);
        }
      }, error => {
        this.utilService.toastErrors(error);
      }
    );
  }

  public handleNotifications(): void {
    this.openedNotifications = !this.openedNotifications;
    if (this.openedNotifications) {
      this.headerService.notificationsMarkAsViewed(
        this.isUserSpanish ? 'contrato.notificationsMarkAsViewed' : 'contrato.notificationsMarkAsViewedPT'
      ).subscribe(
        () => { },
        error => {
          this.utilService.toastErrors(error);
        }
      );
    }
  }

  public menuClicked(event): void {
    this.isMenuOpen = !this.isMenuOpen;
    this.toggleMenu.emit(event);
  }

  public setRouteLabel(): void {
    switch (true) {
      case this.router.url.includes('/customers'):
        this.routeLabel = 'BREADCRUMBS.CUSTOMERS';
        break;
      case this.router.url.includes('/tracing'):
        this.routeLabel = 'BREADCRUMBS.TRACING';
        break;
      case this.router.url.includes('/campaigns'):
        this.routeLabel = 'BREADCRUMBS.CAMPAIGNS';
        break;
      case this.router.url.includes('/incidents'):
        this.routeLabel = 'BREADCRUMBS.INCIDENTS';
        break;
      case this.router.url.includes('/power-optimiser'):
        this.routeLabel = 'BREADCRUMBS.SIMULATIONS';
        break;
      case this.router.url.includes('/co2'):
        this.routeLabel = 'BREADCRUMBS.CO2';
        break;
      case this.router.url.includes('/contracts'):
        this.routeLabel = 'BREADCRUMBS.CONTRACTS';
        break;
      default:
        this.routeLabel = null;
        break;
    }
  }

  public openHomeSearchDialog() {
    const dialogConfig = new MatDialogConfig();

    let typesData: { value: string; label: string }[] = null;
    const typeDefault = 'RazonSocial_Filter';

    if (this.currentRoute === '/home-pymes') {
      typesData = [
        { value: 'Cif_Filter', label: this.i18NextService.t('HOME.SEARCH.CIF') },
        { value: 'Cups_Filter', label: this.i18NextService.t('HOME.SEARCH.CUPS') },
        { value: 'RazonSocial_Filter', label: this.i18NextService.t('HOME.SEARCH.COMPANY_NAME') }
      ];
    } else if (this.currentRoute === '/home-large-accounts') {
      typesData = [
        { value: 'Cif_Filter', label: this.i18NextService.t('HOME.SEARCH.CIF') },
        { value: 'CuentaNegociadora_Filter', label: this.i18NextService.t('HOME.SEARCH.TRADING_ACCOUNT') },
        { value: 'RazonSocial_Filter', label: this.i18NextService.t('HOME.SEARCH.COMPANY_NAME') }
      ];
    }

    dialogConfig.data = {
      disableClose: true,
      autoFocus: true,
      panelClass: ['modal__full-screen'],
      data: { typesData, typeDefault, reseted: true }
    };

    this.dialog.open(HomeSearchComponent, dialogConfig.data);
  }

  public handleNavigate(event: ContractsNotifications): void {
    this.handleNotifications();
    if (event.cambioProducto) {
      this.router.navigate(['/change-product-multipoint'], {
        queryParams:
        {
          idContract: event.codeCRM,
          codeSocialReason: event.socialReason,
          disabledAddSepa: !event.descargadoSEPA,
          bankAccountsSepa: event.cuentasCorrientesSEPA,
          hasChangedDni: event.hasChangedDni,
          hasChangedBankAccount: event.cuentasCorrientesSEPA?.length > 0,
        }
      });
    } else if (event.renovacion) {
      this.router.navigate(['renovate-contract-multipoint'], {
        queryParams: {
          idContract: event.codeCRM,
          codeSocialReason: event.socialReason,
          disabledAddSepa: !event.descargadoSEPA,
          bankAccountsSepa: event.cuentasCorrientesSEPA,
          hasChangedDni: event.hasChangedDni,
          hasChangedBankAccount: event.cuentasCorrientesSEPA?.length > 0,
        }
      });
    } else {
      this.router.navigate(['/new-contract-pymes-multipoint'], {
        queryParams:
        {
          codContract: event.codeCRM,
          codeSocialReason: event.socialReason,
          disabledAddSepa: !event.descargadoSEPA,
          bankAccountsSepa: event.cuentasCorrientesSEPA,
          sepaRequired: event.formPago.toString() === '300000001'
        }
      });
    }
  }

  private registerIcons(): void {
    this.iconRegistry
      .addSvgIcon('help_center', this.sanitizer.bypassSecurityTrustResourceUrl('assets/icons/support-hc.svg'))
      .addSvgIcon('impersonalize', this.sanitizer.bypassSecurityTrustResourceUrl('assets/icons/impersonalize.svg'))
      .addSvgIcon('delete_factors', this.sanitizer.bypassSecurityTrustResourceUrl('assets/icons/lock.svg'))
      .addSvgIcon('user_management', this.sanitizer.bypassSecurityTrustResourceUrl('assets/icons/settings.svg'))
      .addSvgIcon('languages', this.sanitizer.bypassSecurityTrustResourceUrl('assets/icons/chevron.svg'))
      .addSvgIcon('countries', this.sanitizer.bypassSecurityTrustResourceUrl('assets/icons/location.svg'))
      .addSvgIcon('exit', this.sanitizer.bypassSecurityTrustResourceUrl('assets/icons/exit.svg'))
      .addSvgIcon('password', this.sanitizer.bypassSecurityTrustResourceUrl('assets/icons/padlock.svg'));
  }

  public impersonalizeDialog(): void {
    if (!this.authStorage.getItem('current_email')) {
      this.authStorage.setItem('current_email', this.auth.getUserData().email?.toLocaleLowerCase());
    }
    const dialogConfig = new MatDialogConfig();
    dialogConfig.data = {
      disableClose: true,
      autoFocus: false,
      width: '575px',
    };
    const dialogRef = this.dialog.open(ImpersonalizeUserComponent, dialogConfig.data);
    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        window.location.reload();
      }
    });
  }

  public openLanguagesDialog(): void {
    const dialogConfig = new MatDialogConfig();

    dialogConfig.data = {
      disableClose: true,
      autoFocus: false,
      panelClass: [ 'modal__full-screen' ]
    };

    this.dialog.open(LanguagePickerComponent, dialogConfig.data);
  }

  public openCountriesDialog(): void {
    const dialogConfig = new MatDialogConfig();
    dialogConfig.data = {
      autoFocus: false,
      disableClose: true
    };
    const dialogRef = this.dialog.open(ModalChooseCountryComponent, dialogConfig);
    dialogRef.afterClosed().pipe(takeUntil(this.destroyed$)).subscribe((result: string) => {
      if (result) {
        window.location.reload();
      }
    });
  }

  public logout(): void {
    this.auth.logout();
  }

  public downloadFile(id: number): void {
    this.isLoading = true;
    this.headerService.downloadFile(id).pipe(takeUntil(this.destroyed$)).subscribe(
      result => {
        if (result.body.type !== 'application/json') {
          this.toastrService.success('COMMONS.DOWNLOADING_SUCCESS');
          const header = result.headers.get('Content-Disposition');
          const fileName = header.split(';')[1].split('=')[1].replace(/\"/g, '');
          const link = document.createElement('a');
          link.download = fileName;
          link.href = URL.createObjectURL(result.body);
          link.click();
        } else {
          const reader = new FileReader();
          reader.addEventListener('loadend', e => {
            this.toastrService.error(JSON.parse(e.target.result as string).Message);
          });
          reader.readAsText(result.body);
        }
        this.isLoading = false;
      },
      error => {
        this.isLoading = false;
        this.utilService.toastErrors(error);
      }
    );
  }

  public openChangePasswordDialog(): void {
    const dialogConfig = new MatDialogConfig();

    dialogConfig.data = {
      disableClose: true,
      autoFocus: false,
      panelClass: [ 'modal__full-screen' ]
    };

    this.dialog.open(ChangePasswordComponent, dialogConfig.data);
  }

  public openDeleteFactorsDialog(): void {
    const dialogConfig = new MatDialogConfig();
    dialogConfig.data = {
      autoFocus: false,
      disableClose: true
    };
    const dialogRef = this.dialog.open(ModalDeleteFactorsComponent, dialogConfig);
    dialogRef.afterClosed().pipe(takeUntil(this.destroyed$)).subscribe((success: boolean) => {
      if (success) {
        this.logout();
      }
    });
  }

  private subscribeDownloadContractsExcel(): void {
    this.utilService.downloadContractsExcel.pipe(takeUntil(this.destroyed$)).subscribe(
      fileId => {
        if (fileId) {
          this.excelId = fileId;
          this.nofificationsSubscription.unsubscribe();
          this.nofificationsSubscription = timer(0, 10000).subscribe(() => {
            this.getContractsPending(true);
          });
        }
      }
    );
  }

  private finishcheckDownloadContractsExcel(): void {
    const downloadExcelNotification = this.contractsNotifications.find(notification => notification.idFichero === this.excelId);
    if(
      (downloadExcelNotification && downloadExcelNotification.resultOk) ||
      (downloadExcelNotification && !downloadExcelNotification.resultOk && !!downloadExcelNotification.descriptionError)
    ) {
      this.nofificationsSubscription.unsubscribe();
      this.nofificationsSubscription = interval(100000).subscribe(() => {
        this.getContractsPending(false);
      });
    }
  }
}
